import { rootApi } from "../../../lib/api/rootApi";
import type { KioskLoginResponse } from "../types/chipTypes";
import type {
  AttendanceStatus,
  BundleAttendanceRecord,
  BundleStudent,
  KioskBundlePrefill,
  KioskBundlePrefills,
  KioskClassWeekCell,
  KioskClassWeekResponse,
  KioskLessonBundle,
} from "../types/kioskApiTypes";

type KioskBuilder = Parameters<Parameters<typeof rootApi.injectEndpoints>[0]["endpoints"]>[0];

export function buildKioskQueries(builder: KioskBuilder) {
  return {
    // 1. Chip-tap login
    kioskChipLogin: builder.mutation<KioskLoginResponse, { uid: string }>({
      queryFn: async ({ uid }, _api, _extra, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_kiosk_chip_login",
          params: { p_uid: uid },
        });
        if (error) return { error };
        return { data: data as KioskLoginResponse };
      },
    }),

    // 2. Pull everything needed to populate the editor in one round-trip.
    kioskGetLessonBundle: builder.query<
      KioskLessonBundle,
      { lessonId: string; userId: string }
    >({
      queryFn: async ({ lessonId, userId }, _api, _extra, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "get_attendance_bundle_v2",
          params: {
            p_lesson_id: lessonId,
            p_user_id: userId,
            p_flags: null,
          },
        });
        if (error) return { error };

        const raw = (data ?? {}) as Record<string, unknown>;
        const lesson = raw.lesson as { id?: string; school_id?: string } | null;
        const students = (raw.students ?? []) as BundleStudent[];
        const attendance = raw.attendance as
          | { present?: unknown[]; late?: unknown[]; absent?: unknown[] }
          | null;
        const diary = raw.diary as { text?: string | null; homework?: string | null } | null;

        // Flatten the bucketed attendance shape into a flat list keyed by student_id.
        const currentAttendance: BundleAttendanceRecord[] = [];
        const pushBucket = (rows: unknown[] | undefined, fallbackStatus: AttendanceStatus) => {
          for (const row of rows ?? []) {
            const r = row as { student_id?: string; status?: string; notes?: string | null };
            if (!r?.student_id) continue;
            currentAttendance.push({
              student_id: r.student_id,
              status: (r.status as AttendanceStatus) ?? fallbackStatus,
              notes: r.notes ?? null,
            });
          }
        };
        pushBucket(attendance?.present, "present");
        pushBucket(attendance?.late, "late");
        // The bundle's "absent" bucket mixes excused and unexcused — trust the
        // status field on each row to disambiguate.
        pushBucket(attendance?.absent, "absent_unexcused");

        const prefillsRaw = (raw.prefills ?? {}) as Record<string, unknown>;
        // The bundle emits THREE absent buckets: daily_absent_excused
        // (Inf), daily_absent_unexcused (legacy), and daily_absent_unclear
        // (canonical, added by PR #615). The kiosk's 4-status UI has no
        // separate `absent_unclear` — there's only the U button. Union
        // unclear + unexcused into one bucket so ungeklärt students
        // appear in seed-absentees + day rollups instead of being
        // silently dropped. Deduped via Set.
        const dailyUnexcused = (prefillsRaw.daily_absent_unexcused ?? []) as string[];
        const dailyUnclear   = (prefillsRaw.daily_absent_unclear   ?? []) as string[];
        const prefills: KioskBundlePrefills = {
          previous_lesson_status: (prefillsRaw.previous_lesson_status ?? []) as KioskBundlePrefill[],
          krank: (prefillsRaw.krank ?? []) as { student_id: string; reason: string | null }[],
          daily_absent_excused: (prefillsRaw.daily_absent_excused ?? []) as string[],
          daily_absent_unexcused: Array.from(new Set([...dailyUnexcused, ...dailyUnclear])),
          daily_late: (prefillsRaw.daily_late ?? []) as { student_id: string; expected_arrival_time: string | null }[],
          daily_present: (prefillsRaw.daily_present ?? []) as string[],
        };
        const editPerm = raw.edit_permission as { has_existing_attendance?: boolean } | null;

        return {
          data: {
            lesson_id: lesson?.id ?? lessonId,
            school_id: lesson?.school_id ?? "",
            students,
            currentAttendance,
            prefills,
            hasExistingAttendance: !!editPerm?.has_existing_attendance,
            diary: {
              text: diary?.text ?? null,
              homework: diary?.homework ?? null,
            },
            // Pass the untouched RPC response through so kiosk callers can
            // hand it to the shared AttendanceDialog as initialBundle. See
            // KioskLessonBundle docstring for the rationale.
            _raw: data,
          },
        };
      },
    }),

    // 2b. Class-wide week view (Klassenleiter Klassenbuch).
    kioskGetClassWeek: builder.query<
      KioskClassWeekResponse,
      { classId: string; userId: string; weekStart: string /* YYYY-MM-DD Mon */ }
    >({
      queryFn: async ({ classId, userId, weekStart }, _api, _extra, baseQuery) => {
        const { data, error } = await baseQuery({
          type: "rpc",
          rpc: "fn_klassenbuch_get_class_week",
          params: {
            p_class_id:   classId,
            p_user_id:    userId,
            p_week_start: weekStart,
          },
        });
        if (error) return { error };
        // Migration 20260523_kiosk_class_week_calendar_exceptions.sql
        // switched the RPC from returning the raw cells array to
        // returning `{ cells, calendar_exceptions }`. Tolerate both
        // shapes during the rollout window so a cached pre-migration
        // response from another tab can't break the page.
        if (Array.isArray(data)) {
          return {
            data: {
              cells: data as KioskClassWeekCell[],
              calendar_exceptions: [],
            },
          };
        }
        const obj = (data ?? {}) as Partial<KioskClassWeekResponse>;
        return {
          data: {
            cells: (obj.cells ?? []) as KioskClassWeekCell[],
            calendar_exceptions: obj.calendar_exceptions ?? [],
          },
        };
      },
    }),
  };
}
